import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ApiUrlProgramData,
  ApiUrlStep,
  CourseContentService,
  Customisations,
  Hooks,
  ProgramDataCode,
} from '@app/courseContent/courseContent.service';
import {
  Block,
  BlockCheckbox,
  BlockContainer,
  blockerStepFullUrl,
  BlockFeedback,
  BlockRadioBox,
  BlockTextArea,
  BlockTextbox,
  BlockType,
  BlockViaSurvey,
  ButtonType,
  ErrorStepData,
  isErrorStepData,
  isStepData,
  StepData,
  TOKEN_SURVEY_QUERY_PARAM_KEY,
} from '@shared/models/step.model';
import { getUrlWithoutParams } from '@app/utils/url';
import { ButtonConfig } from '@app/components/bottomNavigation/bottomNavigation.component';
import { ButtonVariant } from '@app/components/button/button.component';
import { EntityStatus } from '@shared/models/menu.model';
import { PlIcon } from '@app/icon-library/icons';
import { I18nService } from '@app/i18n';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { IconFrame } from '@app/icon-library/lib/pl-icon.component';
import { opacityAnimation } from '@app/utils/animations';
import { getRoutePath, PlRoute } from '@app/utils/routes';
import { TimeFormat } from '@app/components/pl-timepicker/pl-timepicker.component';
import { ErrorService } from '@shared/services/error.service';
import { PlRouter } from '@shared/services/pl-router.service';
import { ParticipantService } from '@shared/services/participant/participant.service';
import { first, Subscription } from 'rxjs';

export enum FormControlName {
  blocks = 'blocks',
}

interface CourseContentForm {
  [FormControlName.blocks]: FormArray<FormControl<Block>>;
}

export const URL_PARAM_SHOW_PROGRESS_SAVED_BANNER: string = 'showProgressSavedBanner' as const;

export type QueryParamsShowProgressBanner = {
  [key in typeof URL_PARAM_SHOW_PROGRESS_SAVED_BANNER]?: boolean;
};

export interface QueryParamsTokenSurvey {
  token_survey?: string;
}

export type QueryParamsStep = QueryParamsShowProgressBanner & QueryParamsTokenSurvey;

export interface ParamsCourseContentAPICall {
  slug?: string;
  token_survey?: string;
  page?: string;
}

export enum PageType {
  step = 'step',
  friendsSurvey = 'friendsSurvey',
  viaSurvey = 'viaSurvey',
}

@Component({
  selector: 'pl-course-content',
  templateUrl: './courseContent.component.html',
  animations: [opacityAnimation],
  styleUrls: ['./courseContent.component.scss'],
})
export class CourseContentComponent implements OnInit, OnDestroy {
  @Input() public pageType: PageType = PageType.step;
  @Input() public tokenSurvey: string | null = null;
  @Input() public customStepTitle: string | null = null;

  public isStepLoading: WritableSignal<boolean> = signal(false);
  public isStepSaving: WritableSignal<boolean> = signal(false);
  public showProgressSavedBannerToast: WritableSignal<boolean> = signal(false);
  public showProgressHaveBeenSavedBottomBanner: WritableSignal<boolean> = signal(false);
  public isStepLocked: Signal<boolean> = computed(() => this.courseContentService.isCurrentStepLocked());
  public isStepCompleted: Signal<boolean> = computed(() => this.courseContentService.isCurrentStepCompleted());
  public stepData: Signal<StepData | null> = computed(() => this.courseContentService.currentStepData());
  public customisations: Signal<Customisations> = computed(() => this.courseContentService.customisations());
  public participantCurrentStepUrl: Signal<string> = computed(
    () => this.participantService.menu()?.current_progression.current_step?.fullUrl || '',
  );

  public buttonLeftConfig: Signal<ButtonConfig | null> = computed(() =>
    this.getButtonLeftConfig(
      this.stepData(),
      this.isStepLoading() || this.isStepSaving(),
      this.courseContentService.hooks(),
    ),
  );
  public buttonRightConfig: Signal<ButtonConfig | null> = computed(() =>
    this.getButtonRightConfig(this.stepData(), this.isStepLoading() || this.isStepSaving()),
  );

  public form: FormGroup<CourseContentForm>;
  @ViewChild('hiddenSubmitButtonForm', { read: ElementRef }) public hiddenSubmitButtonForm!: ElementRef;
  @ViewChild('myForm', { read: ElementRef }) public myForm!: ElementRef;
  @ViewChild('topDiv', { read: ElementRef }) public topDiv!: ElementRef;

  public participantTimeFormat: Signal<TimeFormat> = computed(() => this.participantService.participantTimeFormat());

  protected readonly BlockType: typeof BlockType = BlockType;
  protected readonly ButtonType: typeof ButtonType = ButtonType;
  protected readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;
  protected readonly FormControlName: typeof FormControlName = FormControlName;
  protected readonly IconFrame: typeof IconFrame = IconFrame;
  protected readonly PageType: typeof PageType = PageType;
  protected readonly PlIcon: typeof PlIcon = PlIcon;

  private currentGetStepDataSubscription: Subscription | null = null;
  private destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private activatedRoute: ActivatedRoute,
    private courseContentService: CourseContentService,
    private errorService: ErrorService,
    private i18nService: I18nService,
    private participantService: ParticipantService,
    private plRouter: PlRouter,
  ) {
    const fb: FormBuilder = new FormBuilder();
    this.form = fb.nonNullable.group<CourseContentForm>({
      blocks: fb.nonNullable.array<Block>([], this.validateFormArray.bind(this)),
    });

    this.form.setValidators(this.validateForm.bind(this));
  }

  public ngOnDestroy(): void {
    this.courseContentService.resetAllCustomisation();
  }

  public ngOnInit(): void {
    this.activatedRoute.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: async (): Promise<void> => {
        const queryParamsSnapshot: QueryParamsStep = this.activatedRoute.snapshot.queryParams as QueryParamsStep;
        if (queryParamsSnapshot[URL_PARAM_SHOW_PROGRESS_SAVED_BANNER]) {
          this.showBrieflyProgressSavedBannerToast();
          this.plRouter.removeQueryParamFromUrl(URL_PARAM_SHOW_PROGRESS_SAVED_BANNER);
        }

        const url: string = this.plRouter.url;
        const slug: string = getUrlWithoutParams(url);
        if (this.pageType === PageType.friendsSurvey) {
          this.retrieveStepDataForFriendsSurvey(slug);
        } else if (this.pageType === PageType.viaSurvey) {
          this.retrieveStepDataForViaSurvey(slug);
        } else {
          this.retrieveStepData(ApiUrlStep.step, slug, { slug }, null);
        }
      },
    });
  }

  public get formArrayBlocks(): FormArray {
    return this.form.get(FormControlName.blocks) as FormArray;
  }

  private retrieveStepDataForFriendsSurvey(slug: string): void {
    const pageNumber: string = slug.slice(-1);

    if (this.tokenSurvey) {
      this.retrieveStepData(
        ApiUrlStep.survey,
        slug,
        {
          page: pageNumber,
          [TOKEN_SURVEY_QUERY_PARAM_KEY]: this.tokenSurvey,
        },
        {
          hideSnackbar: pageNumber === '3',
        },
      );
    } else {
      this.errorService.showError({
        title: this.i18nService.translate(`friends_survey.already_submitted`),
        body: this.i18nService.translate(`friends_survey.contact_support`),
        hideButtons: true,
      });
    }
  }

  private retrieveStepDataForViaSurvey(slug: string): void {
    if (this.tokenSurvey) {
      this.retrieveStepData(
        ApiUrlStep.survey,
        slug,
        {
          [TOKEN_SURVEY_QUERY_PARAM_KEY]: this.tokenSurvey,
        },
        {
          hideBreadcrumbs: this.pageType === PageType.viaSurvey,
        },
      );
    } else {
      this.errorService.showError({
        title: this.i18nService.translate(`friends_survey.already_submitted`),
        body: this.i18nService.translate(`friends_survey.contact_support`),
        hideButtons: true,
      });
    }
  }

  private retrieveStepData(
    apiUrl: ApiUrlStep,
    courseContentSlug: string,
    paramsApi: ParamsCourseContentAPICall,
    forceCustomisations: Customisations | null,
  ): void {
    this.isStepLoading.set(true);
    this.courseContentService.resetCurrentStepData();

    if (this.currentGetStepDataSubscription) {
      this.currentGetStepDataSubscription.unsubscribe();
    }

    this.currentGetStepDataSubscription = this.courseContentService
      .getStepData(apiUrl, courseContentSlug, paramsApi)
      .pipe(first())
      .subscribe({
        next: (stepData: StepData | ErrorStepData): void => {
          if (isStepData(stepData)) {
            this.courseContentService.setCurrentStepData(stepData);
            const isStepLocked: boolean = stepData.status === EntityStatus.locked;
            if (!isStepLocked) {
              this.updateFormFromStepData(stepData);
            }

            this.courseContentService.enableCustomisationsForSpecialSteps(stepData, forceCustomisations);

            this.isStepLoading.set(false);
          } else if (isErrorStepData(stepData)) {
            console.error(`Error while retrieving step data: `, stepData.error);
            throw new Error(`StepData is null, it means there is an error somewhere `);
          } else {
            console.error(`Unexpected type for stepData: `, stepData);
            throw new Error(`Unknown type for step Data`);
          }
        },
      });
  }

  private getButtonLeftConfig(stepData: StepData | null, disableButton: boolean, hooks: Hooks): ButtonConfig | null {
    if (stepData?.prev_page_url || hooks.buttonLeftActionOverride) {
      return {
        variant: ButtonVariant.white,
        iconLeft: PlIcon.chevron_back,
        label: this.i18nService.translate('utils.previous'),
        isDisabled: disableButton,
        action: () => this.buttonLeftClicked(stepData?.prev_page_url, hooks),
      };
    }

    return null;
  }

  public async buttonLeftClicked(prevPageUrl: string | undefined | null, hooks: Hooks): Promise<void> {
    if (hooks.buttonLeftActionOverride) {
      hooks.buttonLeftActionOverride();
    } else {
      this.goToStep(prevPageUrl);
    }
  }

  private getButtonRightConfig(stepData: StepData | null, disableButton: boolean): ButtonConfig | null {
    if (!stepData || stepData.fullUrl === blockerStepFullUrl) {
      return null;
    }

    let label: string = this.i18nService.translate('utils.complete');
    if (stepData.status === EntityStatus.completed) {
      if (stepData.next_page_url === getRoutePath(PlRoute.home)) {
        label = this.i18nService.translate(`utils.home`);
      } else {
        label = this.i18nService.translate(`utils.next`);
      }
    }

    return {
      variant: stepData.status === EntityStatus.completed ? ButtonVariant.blue : ButtonVariant.green,
      label,
      isDisabled: disableButton,
      action: () => this.buttonRightClicked(),
    };
  }

  public async buttonRightClicked(): Promise<void> {
    if (this.isStepCompleted()) {
      this.goToStep(this.stepData()?.next_page_url);
    } else {
      if (
        this.hiddenSubmitButtonForm &&
        this.hiddenSubmitButtonForm.nativeElement &&
        typeof this.hiddenSubmitButtonForm.nativeElement.click === 'function'
      ) {
        this.hiddenSubmitButtonForm.nativeElement.click();
      }
      this.courseContentService.setMainFormSubmitted(true);

      if (this.form.valid) {
        console.log('TONIO CourseContentComponent buttonRightClicked FORM IS VALID this.form=', this.form);
        const hooks: Hooks = this.courseContentService.hooks();
        if (hooks.buttonRightSaveStepOverride) {
          hooks.buttonRightSaveStepOverride();
        } else {
          this.isStepSaving.set(true);
          const stepDataUpdated: StepData = await this.saveCurrentStep();
          if (stepDataUpdated.next_page_url) {
            this.showBannerOrGoToNextStepAfterSavingStep(stepDataUpdated.next_page_url);
          } else {
            throw new Error(`No next_page_url when clicking on right button, this is not normal`);
          }
          this.isStepSaving.set(false);
        }
      }
    }
  }

  private showBannerOrGoToNextStepAfterSavingStep(nextPageUrl: string): void {
    if (nextPageUrl === getRoutePath(PlRoute.home)) {
      this.showProgressHaveBeenSavedBottomBanner.set(true);
    } else {
      console.log(
        '55555555 TONIO CourseContentComponent showBannerOrGoToNextStepAfterSavingStep nextPageUrl=',
        nextPageUrl,
      );
      this.goToStep(nextPageUrl, { [URL_PARAM_SHOW_PROGRESS_SAVED_BANNER]: true });
    }
  }

  private goToStep(stepUrl: string | undefined | null, queryParams?: QueryParamsStep): Promise<boolean> {
    if (stepUrl) {
      if (this.topDiv && this.topDiv.nativeElement && typeof this.topDiv.nativeElement.scrollIntoView === 'function') {
        this.topDiv.nativeElement.scrollIntoView();
      }
      return this.plRouter.navigate([stepUrl], { queryParams });
    }

    return Promise.resolve(false);
  }

  private updateFormFromStepData(stepData: StepData): void {
    this.formArrayBlocks.clear({ emitEvent: false });

    stepData.blocks.forEach((value: Block | null) => {
      if (value) {
        this.formArrayBlocks.push(new FormControl(value), { emitEvent: false });
      }
    });
  }

  protected validateFormArray(abstractControl: AbstractControl): ValidationErrors | null {
    const formArray: FormArray<FormControl<Block>> = abstractControl as FormArray;
    let errors: ValidationErrors | null = null;
    for (const control of formArray.controls) {
      if (control.invalid) {
        if (errors !== null) {
          Object.assign(errors, control.errors);
        } else {
          errors = control.errors;
        }
      }
    }
    return errors;
  }

  protected validateForm(abstractControl: AbstractControl): ValidationErrors | null {
    const formGroup: FormGroup<CourseContentForm> = abstractControl as FormGroup;
    let errors: ValidationErrors | null = null;
    for (const control of Object.values(formGroup.controls)) {
      if (control.invalid) {
        if (errors !== null) {
          Object.assign(errors, control.errors);
        } else {
          errors = control.errors;
        }
      }
    }
    return errors;
  }

  private async saveCurrentStep(): Promise<StepData> {
    const programDataCodes: ProgramDataCode[] = this.getAllProgramDataCodeFromBlocks();
    let apiUrl: ApiUrlProgramData = ApiUrlProgramData.loggedIn;
    if (this.pageType === PageType.friendsSurvey) {
      if (this.tokenSurvey) {
        programDataCodes.push({
          code: TOKEN_SURVEY_QUERY_PARAM_KEY,
          value: this.tokenSurvey,
        });
        apiUrl = ApiUrlProgramData.surveyNotLoggedIn;
      } else {
        throw new Error(`no friendSurveyToken, this is not normal`);
      }
    }

    return await this.courseContentService.saveCurrentStepAndRefreshMenu(
      apiUrl,
      programDataCodes,
      this.formArrayBlocks.value,
      this.pageType === PageType.step,
    );
  }

  private getAllProgramDataCodeFromBlocks(): ProgramDataCode[] {
    const allProgramDataCode: ProgramDataCode[] = [];
    const blocksUpdated: Block[] = this.formArrayBlocks.value;

    blocksUpdated.forEach((block: Block) => {
      allProgramDataCode.push(...this.getProgramDataCodeFromBlock(block));
    });
    return allProgramDataCode;
  }

  private getProgramDataCodeFromBlock(block: Block): ProgramDataCode[] {
    const blockType: BlockType = block.element;

    switch (blockType) {
      case BlockType.container:
        const programDataCodeFromContainerBlock: ProgramDataCode[] = [];
        const containerBlock: BlockContainer = block as BlockContainer;
        for (const blockWithinContainer of containerBlock.data.blocks) {
          programDataCodeFromContainerBlock.push(...this.getProgramDataCodeFromBlock(blockWithinContainer));
        }
        return programDataCodeFromContainerBlock;
      case BlockType.checkbox:
      case BlockType.radiobox:
      case BlockType.textarea:
      case BlockType.textbox:
        const blockWithProgramDataCode: BlockTextbox | BlockTextArea | BlockRadioBox | BlockCheckbox = block as
          | BlockTextbox
          | BlockTextArea
          | BlockRadioBox
          | BlockCheckbox;
        return [
          {
            code: blockWithProgramDataCode.program_data_code,
            value: blockWithProgramDataCode.data.value,
          },
        ];
      case BlockType.feedback:
        const blockFeedback: BlockFeedback = block as BlockFeedback;
        if (blockFeedback.data.rating_value !== null) {
          return [
            {
              code: blockFeedback.program_data_code,
              value: blockFeedback.data.rating_value,
            },
            {
              code: blockFeedback.data.config.program_data_code_feedback_text,
              value: blockFeedback.data.feedback_text,
            },
          ];
        } else {
          return [];
        }
      case BlockType.audio:
      case BlockType.barChart:
      case BlockType.button:
      case BlockType.carousel:
      case BlockType.gaugeChart:
      case BlockType.html:
      case BlockType.iconText:
      case BlockType.lifeAct:
      case BlockType.lifeChanges:
      case BlockType.lifemap:
      case BlockType.moduleOverview:
      case BlockType.spiderChart:
      case BlockType.video:
      case BlockType.wordCloud:
        // For lifeAct, lifeChanges, lifemap block, data is saved live when the user makes changes
        // For all the other, there is no data to be saved
        return [];
      case BlockType.ul:
        // TONIO TODO sometimes there is a program_data_code, don't know why
        // like in http://localhost:4200/potentialife-course/cycle-1/module-2/step-4
        return [];
      case BlockType.viaSurvey:
        const blockViaSurvey: BlockViaSurvey = block as BlockViaSurvey;
        if (blockViaSurvey.data.value) {
          return [
            {
              code: blockViaSurvey.program_data_code,
              value: JSON.parse(blockViaSurvey.data.value),
            },
          ];
        } else {
          console.error(`When saving via survey block, the value should not be empty, block=`, block);
          throw new Error(`ViaSurvey block, value should not be empty`);
        }

      default:
        throw new Error(`Unhandled block type "${blockType}" in getAllProgramDataCodeFromBlocks`);
    }
  }

  private showBrieflyProgressSavedBannerToast(): void {
    this.showProgressSavedBannerToast.set(true);
    setTimeout(() => {
      this.showProgressSavedBannerToast.set(false);
    }, 1500);
  }
}
